import { useEffect, FormEventHandler } from 'react';
import GuestLayout from '@/Layouts/GuestLayout';
import InputError from '@/Components/InputError';
import PrimaryButton from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { Head, Link, router, useForm, usePage } from '@inertiajs/react';
import PasswordInput from '@/Components/PasswordInput';
import MainButton from '@/Components/MainButton';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Transition } from '@headlessui/react';
import { InertiaPage } from '@/types/inertia';

export default function Login({ status, canResetPassword }: { status?: string, canResetPassword: boolean }) {
    const { success } = usePage<InertiaPage>().props.flash

    const { data, setData, post, processing, errors, reset } = useForm({
        email: '',
        password: '',
        remember: false,
    });

    useEffect(() => {
        return () => {
            reset('password');
        };
    }, []);

    useEffect(() => {
        if(status){
            setTimeout(() => {
                router.reload({
                    preserveScroll: true,
                    preserveState: true
                })
            }, 5000)
        }
    }, []);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route('login'));
    };

    return (
        <GuestLayout>
            <Head title="Zaloguj się" />
            <Transition
                show={status ? true : false}
                enter="transition-theme"
                enterFrom="opacity-0"
                leave="transition-theme"
                leaveTo="opacity-0"
            >
                <div className="mb-10 font-medium text-sm bg-emeraldPulse/50 py-6 px-6 relative rounded-lg">
                    <div className="bg-emeraldPulse w-3 h-full absolute left-0 top-0 rounded-l-lg"></div>
                    <p className='text-h5 text-arcticMist font-semibold'>{status}</p>
                </div>
            </Transition>

            <Transition
                show={success ? true : false}
                enter="transition-theme"
                enterFrom="opacity-0"
                leave="transition-theme"
                leaveTo="opacity-0"
            >
                <div className="mb-10 font-medium text-sm bg-emeraldPulse/50 py-6 px-6 relative rounded-lg">
                    <div className="bg-emeraldPulse w-3 h-full absolute left-0 top-0 rounded-l-lg"></div>
                    <p className='text-h5 text-arcticMist font-semibold'>{success}</p>
                </div>
            </Transition>

            <h1 className='h3 text-arcticMist font-bold text-center mb-14 maxMd:mb-12 tracking-[0.64px]'>Zaloguj się</h1>

            <form onSubmit={submit}>
                <div>
                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="w-full"
                        autoComplete="username"
                        isFocused={true}
                        hasError={errors.email}
                        placeholder='Login'
                        icon='ic:baseline-mode-edit-outline'
                        onChange={(e) => setData('email', e.target.value)}
                    />
                    <InputError message={errors.email} />
                </div>

                <div className="mt-6 mb-10">
                    <PasswordInput
                        id="password"
                        name="password"
                        placeholder='Hasło'
                        value={data.password}
                        className="w-full"
                        hasError={errors.password}
                        autoComplete="current-password"
                        onChange={(e) => setData('password', e.target.value)}
                    />
                    <InputError message={errors.password} />
                </div>

                <MainButton className='mb-7 maxMd:mb-4' type='submit' disabled={processing}>
                    Zaloguj się
                </MainButton>

                {canResetPassword && (
                    <div className='text-center mb-8'>
                        <Link
                            href={route('password.request')}
                            className="outline-btn w-full block"
                        >
                            Nie pamiętasz hasła?
                        </Link>
                    </div>
                )}

                <div className="flex items-center text-white relative mb-8">
                    <hr className='border-b-[.5px] border-royalPeriwinkle w-full'/>
                    <span className='mx-4'>albo</span>
                    <hr className='border-b-[.5px] border-royalPeriwinkle w-full'/>
                </div>

                <div className='text-center text-accordorange text-16 leading-6 gap-y-4'>
                    <a href={route('oauth.google')} title='Zaloguj się kontem Google' className='flex justify-center items-center outline-btn mb-4'>
                        <Icon icon={`ri:google-fill`} className='text-arcticMist mr-2' width={24} height={24}/>
                        Zaloguj się kontem Google
                    </a>
                    {/* <Link href='/' title='Zaloguj się kontem Apple' className='flex justify-center items-center outline-btn'>
                        <Icon icon={`ic:baseline-apple`} className='text-arcticMist mr-2' width={24} height={24}/>
                        Zaloguj się kontem Apple
                    </Link> */}
                </div>
            </form>
        </GuestLayout>
    );
}
